import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const dict = {
  en: {
    "nav.how": "How it works",
    "nav.formats": "Formats",
    "nav.faq": "FAQ",
    "nav.open": "Open the studio",
    "nav.back": "Back to home",
    "hero.badge": "Free · No account · Nothing leaves your browser",
    "hero.title1": "Strip AI watermarks",
    "hero.title2": "in your browser.",
    "hero.sub": "Invisible Unicode carriers, C2PA manifests, EXIF/XMP and document properties — detected and removed locally. No upload, no queue, no subscription.",
    "hero.cta": "Clean a file or text",
    "hero.cta2": "See what it removes",
    "hero.stat1": "Processing time",
    "hero.stat1v": "Instant, local",
    "hero.stat2": "Files uploaded",
    "hero.stat2v": "Zero",
    "hero.stat3": "Price",
    "hero.stat3v": "Free forever",
    "counter.label": "cleaned so far by this instance",
    "feat.title": "Two layers, one pass",
    "feat.sub": "The same detection rules as the open-source watermarks-remover skill, ported to WebAssembly-speed JavaScript.",
    "feat.a.title": "Layer A — Invisible Unicode",
    "feat.a.body": "Zero-width spaces, bidi overrides, variation selectors, tag characters and space homoglyphs. Emoji glue and script joiners are preserved by default so your text stays intact.",
    "feat.b.title": "C2PA / Content Credentials",
    "feat.b.body": "JUMBF containers, PNG caBX chunks and JPEG APP11 segments are detected and dropped, with a byte-level scan as a fallback.",
    "feat.c.title": "EXIF / XMP / doc properties",
    "feat.c.body": "PNG text chunks, JPEG APPn segments, PDF XMP packets and Info entries, DOCX docProps and ODT meta.xml.",
    "feat.d.title": "Nothing leaves the device",
    "feat.d.body": "Every byte is parsed by your own browser. There is no upload endpoint to trust, and no server-side copy to delete.",
    "feat.e.title": "Statistical text watermarks",
    "feat.e.body": "Green-list sampling marks are disrupted by rewriting surface tokens, connectors and punctuation — meaning kept, distribution broken. Best-effort by nature.",
    "feat.f.title": "SynthID & pixel watermarks",
    "feat.f.body": "Crop, sub-degree rotation, per-channel dither and lossy re-encode cycles desynchronise the carrier a SynthID-class detector correlates against.",
    "how.title": "How it works",
    "how.1t": "Drop it in",
    "how.1b": "Pick a file or paste your text. Detection starts on the spot — no button needed.",
    "how.2t": "Read the report",
    "how.2b": "Every finding is listed with its class and confidence: confirmed, probable or informational.",
    "how.3t": "Clean and keep",
    "how.3b": "Download the cleaned file or copy the scrubbed text. The original is never modified.",
    "formats.title": "Supported formats",
    "formats.sub": "Raster images, vectors, documents and plain text.",
    "faq.title": "Questions",
    "faq.q1": "Is it really free?",
    "faq.a1": "Yes. There is no account, no quota and no subscription. All the work happens on your machine, so there is nothing to bill.",
    "faq.q2": "Do you keep my files?",
    "faq.a2": "We never receive them. The parsing and rewriting happen entirely in the browser tab; only an anonymous counter of cleaned items is sent.",
    "faq.q3": "Does it remove statistical text watermarks?",
    "faq.a3": "Yes, as a best-effort layer. Statistical watermarks live in the token choices themselves, so the Text tool rewrites surface tokens, punctuation and connectors to break the green-list signal. Always reread the result: no local tool can certify a vendor detector will fail.",
    "faq.q4": "Does it remove pixel watermarks like SynthID?",
    "faq.a4": "It disrupts them, it does not certify their removal. SynthID is spread across the whole image spectrum: Maximum desynchronises the carrier (crop, sub-degree rotation, resampling) and rebuilds the noise layer it hides in, Extreme replaces nearly all of that layer. Only a full diffusion redraw on a GPU is known to defeat it reliably, and that cannot run in a browser. A chatbot can also label an image 'AI' from its look alone — that is not a watermark.",
    "faq.q5": "What about my emojis and non-Latin text?",
    "faq.a5": "Load-bearing invisibles are preserved: ZWJ after an emoji base, ZWNJ inside Persian or Devanagari words, flag tag characters, and orthographic Arabic marks. Paranoid mode strips them anyway.",
    "footer.tagline": "For content you own. Reports separate what is verifiable from what is best-effort.",
    "live.badge": "Live activity",
    "live.label": "files and texts cleaned today",
    "live.perMinute": "per hour right now",
    "live.local": "every one of them processed on its own device",
    "live.feed": "Live stream",
    "live.marks": "marks",
    "live.note": "Activity stream is anonymised and simulated for display: file names never leave the browser they were opened in.",
    "footer.source": "Based on the MIT-licensed",
    "footer.legal": "Legal notice",
    "footer.guides": "Guides",
    "footer.privacy": "Privacy",
    "footer.privacyNote": "Free, no account. Your files and texts are processed by your own browser and never uploaded.",
    "legal.updated": "Last updated",
    "studio.title": "Studio",
    "studio.tabText": "Text",
    "studio.tabFiles": "Files",
    "text.inputLabel": "Your text",
    "text.placeholder": "Paste the text to inspect — detection runs as you type.",
    "text.paste": "Paste from clipboard",
    "text.sample": "Load a marked sample",
    "text.clear": "Clear",
    "text.opts": "Options",
    "text.optSpaces": "Normalise space homoglyphs",
    "text.optNfkc": "NFKC normalisation",
    "text.optConf": "Aggressive confusables (Cyrillic / fullwidth)",
    "text.optGlue": "Paranoid: strip emoji glue too",
    "text.report": "Detection report",
    "text.clean": "Clean the text",
    "text.copy": "Copy result",
    "text.copied": "Copied to clipboard",
    "text.download": "Download .txt",
    "text.chars": "characters",
    "text.suspicious": "suspicious",
    "text.noHits": "No invisible Unicode carrier detected. For sampling watermarks (Claude / GPT class), turn on statistical disruption above.",
    "text.result": "Cleaned text",
    "text.optStat": "Statistical watermark disruption (best-effort)",
    "text.intensity": "Intensity",
    "int.light": "Light",
    "int.balanced": "Balanced",
    "int.strong": "Strong",
    "int.maximum": "Maximum",
    "int.extreme": "Extreme",
    "text.statResult": "Statistical layer",
    "text.statTokens": "tokens rewritten",
    "text.statRatio": "of the text",
    "text.statNote": "Rewriting surface tokens breaks the green-list signal sampling-based watermarks rely on. Read the result before publishing it.",
    "text.favoured": "Favoured-token density",
    "text.per1000": "per 1,000 words",
    "text.reduction": "signal",
    "text.removed": "removed",
    "text.replaced": "replaced",
    "text.highlight": "Highlight carriers",
    "files.drop": "Drop files here",
    "files.or": "or click to browse — nothing is uploaded",
    "files.hint": "PNG, JPEG, SVG, PDF, DOCX, ODT, HTML, Markdown, TXT, MP4, MOV",
    "files.compare": "Before / after",
    "files.optPixel": "Disrupt pixel watermarks (SynthID class)",
    "files.cleanBtn": "Clean the files",
    "files.ready": "ready to clean",
    "files.cleaning": "Cleaning…",
    "files.analysed": "Analysed",
    "files.doneToast": "cleaned",
    "files.pixelNote": "Applies to PNG and JPEG: crop, sub-degree rotation, resample, per-channel dither and lossy re-encode. A cleaned PNG can end up heavier than the original.",
    "files.pixelWarn": "Straight talk: SynthID is a deep watermark spread across the whole image, and no in-browser tool breaks it reliably — only a full diffusion redraw on a GPU does. Here, Maximum desynchronises the carrier and rebuilds the noise layer it hides in; Extreme replaces nearly all of that layer at the cost of visible softening. Also remember a chatbot can call an image \"AI\" from its look alone, which no tool can undo — the real check is Google's SynthID Detector.",
    "files.queue": "Queue",
    "files.marked_one": "mark found",
    "files.clean": "Clean all",
    "files.downloadAll": "Download all",
    "files.download": "Download",
    "files.empty": "Your queue is empty.",
    "files.before": "Before",
    "files.after": "After",
    "files.actions": "Actions taken",
    "files.clean_state": "Clean",
    "files.marked": "marks found",
    "files.unsupported": "Unsupported format",
    "files.processing": "Analysing…",
    "files.remove": "Remove",
    "files.clearAll": "Clear queue",
    "files.saved": "of metadata removed",
    "conf.confirmed": "confirmed",
    "conf.probable": "probable",
    "conf.informational": "info",
    "kind.strip": "invisible",
    "kind.bidi": "bidi control",
    "kind.tag_chars": "tag chars",
    "kind.variation_selector": "variation selector",
    "kind.zwj_family": "zero-width",
    "kind.space": "space homoglyph",
    "kind.confusable": "confusable",
    "kind.other_cf": "format char",
    "disclaimer": "Best-effort by design: this tool reports verifiable removals only and cannot certify that a vendor detector will fail.",
  },
  fr: {
    "nav.how": "Fonctionnement",
    "nav.formats": "Formats",
    "nav.faq": "FAQ",
    "nav.open": "Ouvrir le studio",
    "nav.back": "Retour à l'accueil",
    "hero.badge": "Gratuit · Sans compte · Rien ne quitte votre navigateur",
    "hero.title1": "Effacez les filigranes IA",
    "hero.title2": "dans votre navigateur.",
    "hero.sub": "Caractères Unicode invisibles, manifestes C2PA, EXIF/XMP et propriétés de documents — détectés et supprimés en local. Aucun envoi, aucune file d'attente, aucun abonnement.",
    "hero.cta": "Nettoyer un fichier ou du texte",
    "hero.cta2": "Voir ce qui est supprimé",
    "hero.stat1": "Temps de traitement",
    "hero.stat1v": "Instantané, local",
    "hero.stat2": "Fichiers envoyés",
    "hero.stat2v": "Aucun",
    "hero.stat3": "Prix",
    "hero.stat3v": "Gratuit à vie",
    "counter.label": "nettoyés jusqu'ici par cette instance",
    "feat.title": "Deux couches, un seul passage",
    "feat.sub": "Les mêmes règles de détection que le projet open-source watermarks-remover, portées en JavaScript ultra-rapide.",
    "feat.a.title": "Couche A — Unicode invisible",
    "feat.a.body": "Espaces de largeur nulle, contrôles bidi, sélecteurs de variation, caractères tag et homoglyphes d'espace. Les liants d'emoji et les joncteurs de script sont préservés par défaut.",
    "feat.b.title": "C2PA / Content Credentials",
    "feat.b.body": "Conteneurs JUMBF, chunks PNG caBX et segments JPEG APP11 détectés puis retirés, avec un balayage octet par octet en secours.",
    "feat.c.title": "EXIF / XMP / propriétés",
    "feat.c.body": "Chunks texte PNG, segments APPn JPEG, paquets XMP et entrées Info des PDF, docProps DOCX et meta.xml ODT.",
    "feat.d.title": "Rien ne quitte l'appareil",
    "feat.d.body": "Chaque octet est analysé par votre propre navigateur. Aucun serveur à qui faire confiance, aucune copie à supprimer.",
    "feat.e.title": "Filigranes statistiques (texte)",
    "feat.e.body": "Les marques par échantillonnage sont perturbées en réécrivant tokens de surface, connecteurs et ponctuation — le sens reste, la distribution casse. Par nature en meilleur effort.",
    "feat.f.title": "SynthID & filigranes de pixels",
    "feat.f.body": "Recadrage, rotation infra-degré, tramage par canal et cycles de ré-encodage avec perte désynchronisent le porteur qu'un détecteur type SynthID corrèle.",
    "how.title": "Comment ça marche",
    "how.1t": "Déposez",
    "how.1b": "Choisissez un fichier ou collez votre texte. La détection démarre immédiatement.",
    "how.2t": "Lisez le rapport",
    "how.2b": "Chaque détection est listée avec sa classe et son niveau de confiance : confirmé, probable ou informatif.",
    "how.3t": "Nettoyez",
    "how.3b": "Téléchargez le fichier nettoyé ou copiez le texte. L'original n'est jamais modifié.",
    "formats.title": "Formats pris en charge",
    "formats.sub": "Images matricielles, vectorielles, documents et texte brut.",
    "faq.title": "Questions",
    "faq.q1": "C'est vraiment gratuit ?",
    "faq.a1": "Oui. Aucun compte, aucun quota, aucun abonnement. Tout le travail se fait sur votre machine : il n'y a rien à facturer.",
    "faq.q2": "Conservez-vous mes fichiers ?",
    "faq.a2": "Nous ne les recevons jamais. L'analyse et la réécriture ont lieu entièrement dans l'onglet ; seul un compteur anonyme est envoyé.",
    "faq.q3": "Supprime-t-il les filigranes statistiques dans le texte ?",
    "faq.a3": "Oui, en meilleur effort. Ces marques vivent dans le choix des tokens : l'outil Texte réécrit les tokens de surface, la ponctuation et les connecteurs pour casser le signal de liste verte. Relisez toujours le résultat : aucun outil local ne peut certifier l'échec d'un détecteur.",
    "faq.q4": "Et les filigranes de pixels comme SynthID ?",
    "faq.a4": "Il les perturbe, il ne certifie pas leur suppression. SynthID est réparti dans tout le spectre de l'image : Maximum désynchronise le porteur (recadrage, rotation infra-degré, ré-échantillonnage) et reconstruit la couche de bruit où il se cache, Extrême remplace presque toute cette couche. Seule une régénération complète par diffusion sur GPU le casse de façon fiable, et cela ne peut pas tourner dans un navigateur. Un chatbot peut aussi qualifier une image d'« IA » sur son seul aspect — ce n'est pas un filigrane.",
    "faq.q5": "Mes emojis et mes textes non latins ?",
    "faq.a5": "Les invisibles utiles sont préservés : ZWJ après un emoji, ZWNJ dans les mots persans ou devanagari, caractères tag des drapeaux et marques arabes orthographiques. Le mode paranoïaque les retire quand même.",
    "footer.tagline": "Pour les contenus qui vous appartiennent. Les rapports distinguent le vérifiable du meilleur effort.",
    "live.badge": "Activité en direct",
    "live.label": "fichiers et textes nettoyés aujourd'hui",
    "live.perMinute": "par heure en ce moment",
    "live.local": "chacun traité sur son propre appareil",
    "live.feed": "Flux en direct",
    "live.marks": "marques",
    "live.note": "Le flux d'activité est anonymisé et simulé pour l'affichage : les noms de fichiers ne quittent jamais le navigateur où ils ont été ouverts.",
    "footer.source": "Basé sur le projet MIT",
    "footer.legal": "Mentions légales",
    "footer.guides": "Guides",
    "footer.privacy": "Confidentialité",
    "footer.privacyNote": "Gratuit, sans compte. Vos fichiers et vos textes sont traités par votre propre navigateur et ne sont jamais envoyés.",
    "legal.updated": "Dernière mise à jour",
    "studio.title": "Studio",
    "studio.tabText": "Texte",
    "studio.tabFiles": "Fichiers",
    "text.inputLabel": "Votre texte",
    "text.placeholder": "Collez le texte à inspecter — la détection s'exécute pendant la saisie.",
    "text.paste": "Coller depuis le presse-papiers",
    "text.sample": "Charger un exemple marqué",
    "text.clear": "Vider",
    "text.opts": "Options",
    "text.optSpaces": "Normaliser les homoglyphes d'espace",
    "text.optNfkc": "Normalisation NFKC",
    "text.optConf": "Confusables agressifs (cyrillique / pleine largeur)",
    "text.optGlue": "Paranoïaque : retirer aussi les liants d'emoji",
    "text.report": "Rapport de détection",
    "text.clean": "Nettoyer le texte",
    "text.copy": "Copier le résultat",
    "text.copied": "Copié dans le presse-papiers",
    "text.download": "Télécharger .txt",
    "text.chars": "caractères",
    "text.suspicious": "suspects",
    "text.noHits": "Aucun caractère Unicode invisible détecté. Pour les filigranes par échantillonnage (type Claude / GPT), activez la perturbation statistique ci-dessus.",
    "text.result": "Texte nettoyé",
    "text.optStat": "Perturbation des filigranes statistiques (meilleur effort)",
    "text.intensity": "Intensité",
    "int.light": "Légère",
    "int.balanced": "Équilibrée",
    "int.strong": "Forte",
    "int.maximum": "Maximum",
    "int.extreme": "Extrême",
    "text.statResult": "Couche statistique",
    "text.statTokens": "tokens réécrits",
    "text.statRatio": "du texte",
    "text.statNote": "Réécrire les tokens de surface casse le signal de liste verte utilisé par les filigranes par échantillonnage. Relisez le résultat avant publication.",
    "text.favoured": "Densité de tokens favorisés",
    "text.per1000": "pour 1 000 mots",
    "text.reduction": "de signal",
    "text.removed": "supprimés",
    "text.replaced": "remplacés",
    "text.highlight": "Surligner les vecteurs",
    "files.drop": "Déposez vos fichiers ici",
    "files.or": "ou cliquez pour parcourir — rien n'est envoyé",
    "files.hint": "PNG, JPEG, SVG, PDF, DOCX, ODT, HTML, Markdown, TXT, MP4, MOV",
    "files.compare": "Avant / après",
    "files.optPixel": "Perturber les filigranes de pixels (type SynthID)",
    "files.cleanBtn": "Nettoyer les fichiers",
    "files.ready": "prêt à nettoyer",
    "files.cleaning": "Nettoyage…",
    "files.analysed": "Analysé",
    "files.doneToast": "nettoyé",
    "files.pixelNote": "S'applique aux PNG et JPEG : recadrage, rotation infra-degré, ré-échantillonnage, tramage par canal et ré-encodage avec perte. Un PNG nettoyé peut devenir plus lourd que l'original.",
    "files.pixelWarn": "Soyons clairs : SynthID est un filigrane profond réparti dans toute l'image, et aucun outil dans le navigateur ne le casse de façon fiable — seule une régénération complète par diffusion sur GPU y parvient. Ici, Maximum désynchronise le porteur et reconstruit la couche de bruit ; Extrême la remplace presque entièrement, au prix d'un lissage visible. Et rappelez-vous qu'un chatbot peut qualifier une image d'« IA » sur son seul aspect, ce qu'aucun outil n'efface — le vrai test reste le SynthID Detector de Google.",
    "files.queue": "File d'attente",
    "files.marked_one": "marque trouvée",
    "files.clean": "Tout nettoyer",
    "files.downloadAll": "Tout télécharger",
    "files.download": "Télécharger",
    "files.empty": "Votre file est vide.",
    "files.before": "Avant",
    "files.after": "Après",
    "files.actions": "Actions effectuées",
    "files.clean_state": "Propre",
    "files.marked": "marques trouvées",
    "files.unsupported": "Format non pris en charge",
    "files.processing": "Analyse…",
    "files.remove": "Retirer",
    "files.clearAll": "Vider la file",
    "files.saved": "de métadonnées retirées",
    "conf.confirmed": "confirmé",
    "conf.probable": "probable",
    "conf.informational": "info",
    "kind.strip": "invisible",
    "kind.bidi": "contrôle bidi",
    "kind.tag_chars": "caractère tag",
    "kind.variation_selector": "sélecteur de variation",
    "kind.zwj_family": "largeur nulle",
    "kind.space": "homoglyphe d'espace",
    "kind.confusable": "confusable",
    "kind.other_cf": "caractère de format",
    "disclaimer": "Par conception : cet outil ne rapporte que des suppressions vérifiables et ne peut certifier l'échec d'un détecteur propriétaire.",
  },
};

const I18nContext = createContext(null);

function detectLang() {
  const saved = localStorage.getItem("wm-lang");
  if (saved === "fr" || saved === "en") return saved;
  const nav = (navigator.languages && navigator.languages[0]) || navigator.language || "en";
  return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
}

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(detectLang);
  useEffect(() => {
    localStorage.setItem("wm-lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(
    () => ({ lang, setLang, t: (key) => dict[lang][key] ?? dict.en[key] ?? key }),
    [lang]
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}
